import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';
import {
  Calculator,
  CheckIcon,
  Info,
  Package,
  PencilIcon,
  TrashIcon,
  TriangleAlert,
  XIcon,
} from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Button } from 'components/Button';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import type {
  BaseOptionType,
  SelectValue as SelectValueType,
} from 'components/Select';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Input } from 'components/input';
import { RHFCommitOnBlurNumberInput } from 'components/input/RHFCommitOnBlurNumberInput';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  LTLItem,
  LTL_FREIGHT_CLASSES,
  LTL_UNIT_SYSTEMS,
  PACKAGE_CODE_TO_TYPE,
  PACKAGE_TYPE_TO_CODE,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { TMSCommodity } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import { formatUpToFiveDecimals } from '../../utils/formatting';
import {
  CUBIC_METERS_PER_CUBIC_FOOT,
  adjustItemDimensionsForCubicFeet,
  calculateLTLMetrics,
} from '../../utils/ltlMetrics';
import { getColorForItemId } from '../components/visualizationColors';
import { useIs3DVisualisationEnabled } from '../hooks/useLTLQuickQuoteConfigFlags';
import {
  useAutoOpenSplitSideLoadRow,
  useSideLoadItemStatusTag,
} from '../hooks/useSideLoadItemStatusTag';
import { useTaiFreightClassWarning } from './utils/freightClassWarning';

interface ItemCardProps {
  index: number;
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  itemColorMap: ReadonlyMap<string, string>;
  onRemove: () => void;
  commodities: TMSCommodity[];
  isLoadingCommodities: boolean;
  isCatalogReady?: boolean;
  hasCustomer: boolean;
  hasTmsId: boolean;
  hasCustomerId: boolean;
}

const ANIMATIONS = {
  card: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
  toggle: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.15 },
  },
  actions: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.1 },
  },
};

const FIELD_PATH = 'ltl.items' as const;

type DescriptionOption = BaseOptionType & {
  freightClass?: string;
  nmfcCode?: string;
  commodityName?: string;
  isCustom?: boolean;
  renderLabel?: string;
};

interface CommoditySelectOptions {
  preserveExisting?: boolean;
  preserveDescriptionSource?: boolean;
}

function isValidNumber(val: unknown): boolean {
  if (typeof val !== 'number' && typeof val !== 'string') {
    return false;
  }

  const numberValue = Number(val);
  return Number.isFinite(numberValue) && numberValue > 0;
}

function getCommodityDisplayName(c: TMSCommodity): string {
  return c.commodityDescription ?? c.commodity ?? c.externalTMSID ?? '';
}

/**
 * LTL item card. Commodity dropdown populates description and section-level
 * freight class/NMFC (ltl.commodity). Includes package type and dimensions/weight
 * with units from form (metric/imperial).
 */
export function ItemCard({
  index,
  formMethods,
  itemColorMap,
  onRemove,
  commodities,
  isLoadingCommodities,
  isCatalogReady = true,
  hasCustomer,
  hasTmsId,
  hasCustomerId,
}: ItemCardProps): JSX.Element {
  const {
    setValue,
    resetField,
    control,
    formState: { errors, defaultValues },
  } = formMethods;
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<Partial<LTLItem>>>(null);
  const [catalogAppliedFields, setCatalogAppliedFields] = useState<
    ReadonlySet<keyof LTLItem>
  >(new Set());
  const catalogAppliedFieldsBeforeEditRef = useRef<ReadonlySet<keyof LTLItem>>(
    new Set()
  );
  const [autoCalculateCubicFeet, setAutoCalculateCubicFeet] = useState(true);
  const [cubicFeetInput, setCubicFeetInput] = useState<string>('');
  const [searchValue, setSearchValue] = useState('');
  const showVisualizationColor = useIs3DVisualisationEnabled();

  // Use same source as top section (useWatch) so item state includes all nested field values
  const items = useWatch({ control, name: 'ltl.items' }) ?? [];
  const watchedItem = items[index];
  const defaultItem = defaultValues?.ltl?.items?.find(
    (item) => item?.itemId === watchedItem?.itemId
  );
  const selectedUnits =
    useWatch({ control, name: 'ltl.commodity.units' }) ??
    LTL_UNIT_SYSTEMS.IMPERIAL;
  const isMetric = selectedUnits === LTL_UNIT_SYSTEMS.METRIC;
  const { renderSideLoadStatusTag } = useSideLoadItemStatusTag({
    control,
    itemIndex: index,
    parentItemLabelPrefix: 'Commodity',
  });
  const isEmptyItem = !watchedItem?.description?.trim();
  /** Per-item freight class so the Tai warning is scoped to this item only. */
  const selectedFreightClass = watchedItem?.freightClass;

  // Normalize item so dimensions/weight are numbers (form may store strings from inputs)
  const normalizedItemForMetrics = useMemo((): LTLItem[] => {
    if (!watchedItem) {
      return [];
    }
    const len = Number(watchedItem.length);
    const w = Number(watchedItem.width);
    const h = Number(watchedItem.height);
    const weight = Number(watchedItem.weight);
    const hu = Math.max(1, Number(watchedItem.handlingUnits) || 1);
    return [
      {
        ...watchedItem,
        length: len > 0 ? len : undefined,
        width: w > 0 ? w : undefined,
        height: h > 0 ? h : undefined,
        weight: weight > 0 ? weight : undefined,
        handlingUnits: hu,
      },
    ];
  }, [watchedItem]);

  const itemMetrics = useMemo(
    () =>
      calculateLTLMetrics(
        normalizedItemForMetrics,
        selectedUnits as (typeof LTL_UNIT_SYSTEMS)[keyof typeof LTL_UNIT_SYSTEMS],
        {
          useLayoutBoundingBoxForCubicFeet: false,
        }
      ),
    [normalizedItemForMetrics, selectedUnits]
  );

  const itemCubicVolume = useMemo(() => {
    const totalCubicFeet = itemMetrics.totalCubicFeet;
    if (totalCubicFeet <= 0) {
      return 0;
    }
    return isMetric
      ? totalCubicFeet * CUBIC_METERS_PER_CUBIC_FOOT
      : totalCubicFeet;
  }, [itemMetrics.totalCubicFeet, isMetric]);

  useEffect(() => {
    if (autoCalculateCubicFeet) {
      setCubicFeetInput(
        itemCubicVolume > 0 ? formatUpToFiveDecimals(itemCubicVolume) : ''
      );
    }
  }, [autoCalculateCubicFeet, itemCubicVolume]);

  const handleCubicFeetBlur: React.FocusEventHandler<HTMLInputElement> = () => {
    if (autoCalculateCubicFeet) {
      return;
    }

    const parsed = parseFloat(cubicFeetInput);

    if (!watchedItem || !Number.isFinite(parsed) || parsed <= 0) {
      return;
    }
    const targetTotalCubicFeet = isMetric
      ? parsed / CUBIC_METERS_PER_CUBIC_FOOT
      : parsed;

    const updated = adjustItemDimensionsForCubicFeet(
      watchedItem as LTLItem,
      selectedUnits as (typeof LTL_UNIT_SYSTEMS)[keyof typeof LTL_UNIT_SYSTEMS],
      targetTotalCubicFeet
    );

    if (updated.length && updated.length > 0) {
      setValue(`${FIELD_PATH}.${index}.length`, updated.length, {
        shouldValidate: true,
      });
    }
    if (updated.width && updated.width > 0) {
      setValue(`${FIELD_PATH}.${index}.width`, updated.width, {
        shouldValidate: true,
      });
    }
    if (updated.height && updated.height > 0) {
      setValue(`${FIELD_PATH}.${index}.height`, updated.height, {
        shouldValidate: true,
      });
    }

    // Keep the displayed cubic feet value normalized after blur
    setCubicFeetInput(formatUpToFiveDecimals(parsed));
  };

  const itemBasePath = `ltl.items.${index}` as const;
  const freightClassPath = `${itemBasePath}.freightClass` as const;
  const taiWarning = useTaiFreightClassWarning({
    items: normalizedItemForMetrics,
    units:
      selectedUnits as (typeof LTL_UNIT_SYSTEMS)[keyof typeof LTL_UNIT_SYSTEMS],
    useLayoutBoundingBoxForCubicFeet: false,
    selectedFreightClass,
    usingEstimatedFreightClass: watchedItem?.usingEstimatedFreightClass,
    setValue,
    freightClassPath,
    estimatedFreightClassPath: `${itemBasePath}.estimatedFreightClass` as const,
    usingEstimatedFreightClassPath:
      `${itemBasePath}.usingEstimatedFreightClass` as const,
    showEstimatedFreightClassMessagePath:
      `${itemBasePath}.showEstimatedFreightClassMessage` as const,
  });

  const dimensionUnit = isMetric ? 'cm' : 'in';
  const weightUnit = isMetric ? 'kg' : 'lb';

  useEffect(() => {
    if (isEmptyItem && !isEditing) {
      setIsEditing(true);
    }
  }, [isEmptyItem, isEditing]);

  useAutoOpenSplitSideLoadRow({
    watchedItem,
    isEditing,
    setIsEditing,
  });

  useEffect(() => {
    if (
      watchedItem?.packageType === undefined ||
      watchedItem?.packageType === null
    ) {
      setValue(
        `${FIELD_PATH}.${index}.packageType`,
        PACKAGE_TYPE_TO_CODE['Pallet'],
        {
          shouldValidate: false,
        }
      );
    }
  }, [index, setValue, watchedItem?.packageType]);

  const handleCommoditySelect = (
    externalTMSID: string,
    options?: CommoditySelectOptions
  ) => {
    const commodity = commodities.find(
      (c) => c.externalTMSID === externalTMSID
    );
    if (commodity) {
      const preserveExisting = options?.preserveExisting ?? false;
      const nextCatalogAppliedFields = new Set(catalogAppliedFields);
      const shouldApplyCatalogField = (
        field: keyof LTLItem,
        hasExistingValue: boolean
      ) =>
        !preserveExisting ||
        !hasExistingValue ||
        catalogAppliedFields.has(field);
      const handlingUnitsValue = Number(watchedItem?.handlingUnits);
      const piecesValue = Number(watchedItem?.pieces);
      const currentHandlingUnits = Number.isFinite(handlingUnitsValue)
        ? handlingUnitsValue
        : 0;
      const currentPieces = Number.isFinite(piecesValue) ? piecesValue : 0;

      setValue(
        `${FIELD_PATH}.${index}.description`,
        getCommodityDisplayName(commodity),
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      if (!options?.preserveDescriptionSource) {
        nextCatalogAppliedFields.add('description');
      }
      const hasExplicitFreightClass =
        Boolean(watchedItem?.freightClass?.trim()) &&
        !watchedItem?.usingEstimatedFreightClass;
      if (
        !preserveExisting ||
        (commodity.freightClass?.trim() &&
          shouldApplyCatalogField('freightClass', hasExplicitFreightClass))
      ) {
        setValue(
          `${FIELD_PATH}.${index}.freightClass`,
          commodity.freightClass ?? '',
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        nextCatalogAppliedFields.add('freightClass');
        setValue(`${FIELD_PATH}.${index}.usingEstimatedFreightClass`, false, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
      const commodityNmfc = commodity.nmfc ?? commodity.nmfcNumber;
      if (
        !preserveExisting ||
        (commodityNmfc?.trim() &&
          shouldApplyCatalogField(
            'nmfcCode',
            Boolean(watchedItem?.nmfcCode?.trim())
          ))
      ) {
        setValue(`${FIELD_PATH}.${index}.nmfcCode`, commodityNmfc ?? '', {
          shouldValidate: true,
          shouldDirty: true,
        });
        nextCatalogAppliedFields.add('nmfcCode');
      }
      // Auto-populate item-level details from commodity
      if (
        commodity.length != null &&
        commodity.length > 0 &&
        shouldApplyCatalogField('length', isValidNumber(watchedItem?.length))
      ) {
        setValue(`${FIELD_PATH}.${index}.length`, commodity.length, {
          shouldValidate: true,
          shouldDirty: true,
        });
        nextCatalogAppliedFields.add('length');
      }
      if (
        commodity.width != null &&
        commodity.width > 0 &&
        shouldApplyCatalogField('width', isValidNumber(watchedItem?.width))
      ) {
        setValue(`${FIELD_PATH}.${index}.width`, commodity.width, {
          shouldValidate: true,
          shouldDirty: true,
        });
        nextCatalogAppliedFields.add('width');
      }
      if (
        commodity.height != null &&
        commodity.height > 0 &&
        shouldApplyCatalogField('height', isValidNumber(watchedItem?.height))
      ) {
        setValue(`${FIELD_PATH}.${index}.height`, commodity.height, {
          shouldValidate: true,
          shouldDirty: true,
        });
        nextCatalogAppliedFields.add('height');
      }
      if (
        commodity.weightAmount != null &&
        commodity.weightAmount > 0 &&
        shouldApplyCatalogField('weight', isValidNumber(watchedItem?.weight))
      ) {
        setValue(`${FIELD_PATH}.${index}.weight`, commodity.weightAmount, {
          shouldValidate: true,
          shouldDirty: true,
        });
        nextCatalogAppliedFields.add('weight');
      }
      if (
        commodity.handlingUnitCount != null &&
        commodity.handlingUnitCount > 0 &&
        shouldApplyCatalogField('handlingUnits', currentHandlingUnits > 0)
      ) {
        setValue(
          `${FIELD_PATH}.${index}.handlingUnits`,
          commodity.handlingUnitCount,
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        nextCatalogAppliedFields.add('handlingUnits');
      }
      if (
        commodity.pieceCount != null &&
        commodity.pieceCount > 0 &&
        shouldApplyCatalogField('pieces', currentPieces > 0)
      ) {
        setValue(`${FIELD_PATH}.${index}.pieces`, commodity.pieceCount, {
          shouldValidate: true,
          shouldDirty: true,
        });
        nextCatalogAppliedFields.add('pieces');
      }
      // Map package type if commodity has handlingUnitTypeId matching our codes
      if (
        commodity.handlingUnitTypeId != null &&
        PACKAGE_CODE_TO_TYPE[commodity.handlingUnitTypeId] &&
        shouldApplyCatalogField(
          'packageType',
          preserveExisting
            ? defaultItem?.packageType != null
            : watchedItem?.packageType != null
        )
      ) {
        setValue(
          `${FIELD_PATH}.${index}.packageType`,
          commodity.handlingUnitTypeId,
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        nextCatalogAppliedFields.add('packageType');
      }
      // Always update commodityExternalTmsId to the selected commodity's TMS ID
      setValue(`${FIELD_PATH}.${index}.commodityExternalTmsId`, externalTMSID, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setCatalogAppliedFields(nextCatalogAppliedFields);
    }
  };

  const canUseCommodityField =
    hasCustomer && hasCustomerId && hasTmsId && !isLoadingCommodities;

  const descriptionPlaceholder = !hasCustomer
    ? 'Select a customer first to load commodities'
    : !hasCustomerId || !hasTmsId
      ? 'Commodities unavailable for this customer'
      : isLoadingCommodities
        ? 'Loading commodities...'
        : commodities.length === 0
          ? 'Type a commodity description'
          : 'Select or type a description';

  const commodityOptions = useMemo<DescriptionOption[]>(
    () =>
      commodities.map((c) => ({
        label: getCommodityDisplayName(c),
        value: c.externalTMSID,
        freightClass: c.freightClass,
        nmfcCode: c.nmfc ?? c.nmfcNumber,
        commodityName: c.commodity,
      })),
    [commodities]
  );

  const descriptionValue = watchedItem?.description?.trim() ?? '';

  const selectedCommodity = useMemo(() => {
    if (!descriptionValue) {
      return undefined;
    }

    return commodities.find(
      (c) =>
        getCommodityDisplayName(c) === descriptionValue ||
        c.externalTMSID === descriptionValue
    );
  }, [commodities, descriptionValue]);

  const autoAppliedCommodityRef = useRef('');

  useEffect(() => {
    if (!isCatalogReady || isLoadingCommodities || !canUseCommodityField) {
      return;
    }

    const currentCommodityId = watchedItem?.commodityExternalTmsId;
    const hasCurrentCatalogCommodity = Boolean(
      currentCommodityId &&
      commodities.some(
        (commodity) => commodity.externalTMSID === currentCommodityId
      )
    );

    if (currentCommodityId && !hasCurrentCatalogCommodity) {
      setValue(`${FIELD_PATH}.${index}.commodityExternalTmsId`, '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (!selectedCommodity || hasCurrentCatalogCommodity) {
      return;
    }

    const defaultDescription = defaultItem?.description?.trim() ?? '';
    const matchKey = `${watchedItem?.itemId}:${selectedCommodity.externalTMSID}`;

    if (
      !defaultDescription ||
      defaultDescription.toLowerCase() !== descriptionValue.toLowerCase() ||
      autoAppliedCommodityRef.current === matchKey
    ) {
      return;
    }

    handleCommoditySelect(selectedCommodity.externalTMSID, {
      preserveExisting: true,
      preserveDescriptionSource: true,
    });
    autoAppliedCommodityRef.current = matchKey;
  }, [
    defaultItem?.description,
    descriptionValue,
    handleCommoditySelect,
    index,
    isCatalogReady,
    isLoadingCommodities,
    canUseCommodityField,
    commodities,
    selectedCommodity,
    watchedItem?.commodityExternalTmsId,
    watchedItem?.itemId,
  ]);

  const selectValue = selectedCommodity
    ? selectedCommodity.externalTMSID
    : descriptionValue
      ? `custom:${descriptionValue}`
      : '';

  const selectOptions = useMemo(() => {
    const options = [...commodityOptions];
    const normalizedSearch = searchValue.trim();
    const isSearchMatchingCommodity = normalizedSearch
      ? commodityOptions.some((option) => {
          const label = String(option.label).toLowerCase();
          return (
            label === normalizedSearch.toLowerCase() ||
            String(option.value).toLowerCase() ===
              normalizedSearch.toLowerCase()
          );
        })
      : false;
    const customOption =
      descriptionValue && !selectedCommodity
        ? {
            label: descriptionValue,
            value: `custom:${descriptionValue}`,
            isCustom: true,
            renderLabel: descriptionValue,
          }
        : null;
    const searchOption =
      normalizedSearch && !isSearchMatchingCommodity
        ? {
            label: normalizedSearch,
            value: `custom:${normalizedSearch}`,
            isCustom: true,
            renderLabel: `Use "${normalizedSearch}"`,
          }
        : null;

    // Keeps the popover from showing only “No results” when the Tai catalog is empty.
    const emptyCatalogHintOption =
      canUseCommodityField &&
      commodityOptions.length === 0 &&
      !normalizedSearch &&
      !descriptionValue
        ? {
            label:
              'Type in the search field, then select Use "your text" below',
            value: '__tai_commodity_empty_catalog_hint__',
            disabled: true,
          }
        : null;

    if (
      customOption &&
      !options.some((opt) => opt.value === customOption.value)
    ) {
      options.unshift(customOption);
    }

    if (
      searchOption &&
      !options.some((opt) => opt.value === searchOption.value)
    ) {
      options.unshift(searchOption);
    }

    if (
      emptyCatalogHintOption &&
      !options.some((opt) => opt.value === emptyCatalogHintOption.value)
    ) {
      options.unshift(emptyCatalogHintOption);
    }

    return options;
  }, [
    canUseCommodityField,
    commodityOptions,
    descriptionValue,
    searchValue,
    selectedCommodity,
  ]);

  const filterOption = (input: string, option?: BaseOptionType) => {
    if (!option) {
      return false;
    }

    const typedOption = option as DescriptionOption;
    if (typedOption.isCustom) {
      return true;
    }

    const search = input.toLowerCase();

    return (
      String(typedOption.label ?? '')
        .toLowerCase()
        .includes(search) ||
      (typedOption.commodityName ?? '').toLowerCase().includes(search) ||
      (typedOption.nmfcCode ?? '').toLowerCase().includes(search)
    );
  };

  const handleDescriptionSelect = (
    value: SelectValueType,
    option?: BaseOptionType | BaseOptionType[]
  ) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const selectedValue =
      typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : '';

    if (selectedOption?.isCustom) {
      const customLabel = String(selectedOption.label ?? '').trim();

      if (defaultItem && customLabel === defaultItem.description) {
        const fieldsToReset: (keyof LTLItem)[] = [
          'description',
          'freightClass',
          'nmfcCode',
          'commodityExternalTmsId',
          'length',
          'width',
          'height',
          'weight',
          'handlingUnits',
          'pieces',
          'canSideLoad',
          'parentItemId',
          'packageType',
        ];
        fieldsToReset.forEach((key) => {
          resetField(`${FIELD_PATH}.${index}.${key}` as any);
        });
      } else {
        const hadSelectedCommodity = Boolean(
          watchedItem?.commodityExternalTmsId
        );

        setValue(`${FIELD_PATH}.${index}.description`, customLabel, {
          shouldValidate: true,
          shouldDirty: true,
        });

        if (hadSelectedCommodity) {
          setValue(`${FIELD_PATH}.${index}.freightClass`, '', {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.nmfcCode`, '', {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.length`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.width`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.height`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.weight`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.handlingUnits`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.pieces`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.packageType`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }

        setValue(`${FIELD_PATH}.${index}.commodityExternalTmsId`, '', {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      setCatalogAppliedFields(new Set());
      setSearchValue('');
      return;
    }

    const commodity = commodities.find(
      (c) => c.externalTMSID === selectedValue
    );
    if (commodity) {
      handleCommoditySelect(commodity.externalTMSID);
    }
    setSearchValue('');
  };

  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedItem });
      catalogAppliedFieldsBeforeEditRef.current = catalogAppliedFields;
      setIsEditing(true);
    },
    save: () => {
      setIsEditing(false);
      setOriginalValues(null);
      catalogAppliedFieldsBeforeEditRef.current = new Set();
    },
    discard: () => {
      if (originalValues) {
        Object.keys(originalValues).forEach((key) => {
          const fieldName = `${FIELD_PATH}.${index}.${key}` as any;
          const value = originalValues[key as keyof LTLItem];
          if (value !== undefined) {
            setValue(fieldName, value, { shouldValidate: true });
          }
        });
      }
      setCatalogAppliedFields(catalogAppliedFieldsBeforeEditRef.current);
      setIsEditing(false);
      setOriginalValues(null);
      catalogAppliedFieldsBeforeEditRef.current = new Set();
    },
    remove: onRemove,
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && isEditing) {
      e.preventDefault();
      e.stopPropagation();
      handlers.save();
    }
  };

  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {!isEmptyItem ? (
            <>
              <ActionButton
                onClick={handlers.save}
                className='hover:bg-info-200 text-info-600'
                icon={CheckIcon}
                aria-label='Save changes'
              />
              <ActionButton
                onClick={handlers.discard}
                className='hover:bg-error-200 text-error-600'
                icon={XIcon}
                aria-label='Discard changes'
              />
            </>
          ) : (
            <ActionButton
              onClick={handlers.remove}
              className='hover:bg-error-300 text-error-600'
              icon={TrashIcon}
              aria-label='Remove item'
            />
          )}
        </motion.div>
      ) : (
        <motion.div
          key='view-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          <ActionButton
            onClick={handlers.edit}
            className='hover:bg-info-300 text-info-600'
            icon={PencilIcon}
            aria-label='Edit item'
          />
          <ActionButton
            onClick={handlers.remove}
            className='hover:bg-error-400 text-error-600'
            icon={TrashIcon}
            aria-label='Remove item'
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      <Flex justify='between' align='center' className='mb-3'>
        <Flex align='center' gap='sm' className='mr-2'>
          <Typography
            variant='body-sm'
            weight='medium'
            className='whitespace-nowrap shrink-0'
          >
            Edit Item
          </Typography>
          {renderSideLoadStatusTag('max-w-[150px]')}
        </Flex>
        <div onClick={(e) => e.stopPropagation()} className='shrink-0'>
          {renderActions()}
        </div>
      </Flex>

      <div className='mb-4'>
        <Label
          name={`${FIELD_PATH}.${index}.description`}
          required
          hideAIHint={catalogAppliedFields.has('description')}
        >
          Description
        </Label>
        <Controller
          name={`${FIELD_PATH}.${index}.description`}
          control={control}
          rules={{ required: 'Description is required' }}
          render={() => (
            <Select
              value={selectValue}
              onChange={handleDescriptionSelect}
              options={selectOptions}
              showSearch
              onSearch={setSearchValue}
              placeholder={descriptionPlaceholder}
              className='w-full mt-1'
              filterOption={filterOption}
              optionRender={(option: DescriptionOption) => {
                if (option.isCustom) {
                  return (
                    <span className='block whitespace-normal break-words'>
                      {option.renderLabel ?? option.label}
                    </span>
                  );
                }

                return (
                  <span className='block whitespace-normal break-words'>
                    {option.label}
                    {option.freightClass ? ` (CL ${option.freightClass})` : ''}
                  </span>
                );
              }}
            />
          )}
        />
        {canUseCommodityField && (
          <Typography variant='body-xs' className='text-neutral-500 mt-1'>
            Not in your Tai catalog? Just type the description.
          </Typography>
        )}
        {errors.ltl?.items?.[index]?.description && (
          <Typography variant='body-xs' className='text-error-500 mt-1'>
            {errors.ltl.items[index].description.message}
          </Typography>
        )}
      </div>

      <Grid cols='2' gap='lg' className='w-full mx-0 mb-4'>
        <div>
          <Label
            name={`${FIELD_PATH}.${index}.freightClass`}
            required
            hideAIHint={catalogAppliedFields.has('freightClass')}
          >
            Freight Class
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.freightClass`}
            control={control}
            rules={{ required: 'Freight class is required' }}
            render={({ field }) => (
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  setValue(
                    `${FIELD_PATH}.${index}.usingEstimatedFreightClass`,
                    false,
                    {
                      shouldValidate: false,
                      shouldDirty: false,
                    }
                  );
                }}
                value={field.value ?? ''}
              >
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Select class' />
                </SelectTrigger>
                <SelectContent>
                  {LTL_FREIGHT_CLASSES.map((fc) => (
                    <SelectItem key={fc} value={fc}>
                      {fc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.ltl?.items?.[index]?.freightClass && (
            <Typography variant='body-xs' className='text-error-500 mt-1'>
              {errors.ltl?.items?.[index]?.freightClass?.message}
            </Typography>
          )}
        </div>
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.nmfcCode`}
          label='NMFC'
          placeholder='e.g., 62120-01'
          hideAIHint={catalogAppliedFields.has('nmfcCode')}
        />
        {/* Per-item volume (Cubic Feet or CBM) and Density (PCF) - always visible in Edit Item box */}
        <div>
          <Flex align='center' gap='xs'>
            <Label name='ltl.item.cubicFeet' required={false}>
              {isMetric ? 'Cubic Meters (CBM)' : 'Cubic Feet'}
            </Label>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 cursor-help text-neutral-400' />
              </TooltipTrigger>
              <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                <p>
                  {isMetric
                    ? 'Cubic meters (CBM) for this item (length × width × height × handling units).'
                    : 'Cubic feet for this item (length × width × height × handling units).'}
                </p>
              </TooltipContent>
            </Tooltip>
          </Flex>
          <Flex gap='xs' className='w-full mx-0 mt-1'>
            <Input
              type='number'
              min={0}
              step='0.01'
              value={cubicFeetInput}
              onChange={(e) => {
                if (!autoCalculateCubicFeet) {
                  setCubicFeetInput(e.target.value);
                }
              }}
              onBlur={handleCubicFeetBlur}
              readOnly={autoCalculateCubicFeet}
              className='flex-1'
            />
            <Tooltip delayDuration={10}>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  size='auto'
                  onClick={() => setAutoCalculateCubicFeet((prev) => !prev)}
                  className='shrink-0 h-8 w-8'
                  buttonNamePosthog={null}
                >
                  <Calculator
                    className={cn(
                      'h-4 w-4',
                      autoCalculateCubicFeet
                        ? 'text-brand-600'
                        : 'text-warning-500'
                    )}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                {autoCalculateCubicFeet
                  ? isMetric
                    ? 'Auto-calculate enabled. Click to enter cubic meters manually.'
                    : 'Auto-calculate enabled. Click to enter cubic feet manually.'
                  : isMetric
                    ? 'Manual cubic meters. Click to return to auto-calculated value.'
                    : 'Manual cubic feet. Click to return to auto-calculated value.'}
              </TooltipContent>
            </Tooltip>
          </Flex>
        </div>
        <div>
          <Flex align='center' gap='xs'>
            <Label name='ltl.item.pcf' required={false}>
              Density (PCF)
            </Label>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 cursor-help text-neutral-400' />
              </TooltipTrigger>
              <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                <p>
                  Density (PCF) for this item (lbs/ft³), used to estimate
                  freight class.
                </p>
              </TooltipContent>
            </Tooltip>
          </Flex>
          <Input
            readOnly
            value={
              itemMetrics.pcf > 0
                ? `${formatUpToFiveDecimals(itemMetrics.pcf)} lbs/ft³`
                : '—'
            }
            className='mt-1'
          />
        </div>
      </Grid>

      <Grid cols='3' gap='lg' className='w-full mx-0 mb-4'>
        <RHFCommitOnBlurNumberInput
          name={`${FIELD_PATH}.${index}.length`}
          label='Length'
          placeholder='0'
          aiIconOnly={true}
          hideAIHint={catalogAppliedFields.has('length')}
          min={0}
          step={1}
          inlineDimensionUnit={dimensionUnit}
          data-testid={`${FIELD_PATH}.${index}.length`}
        />
        <RHFCommitOnBlurNumberInput
          name={`${FIELD_PATH}.${index}.width`}
          label='Width'
          placeholder='0'
          aiIconOnly={true}
          hideAIHint={catalogAppliedFields.has('width')}
          min={0}
          step={1}
          inlineDimensionUnit={dimensionUnit}
          data-testid={`${FIELD_PATH}.${index}.width`}
        />
        <RHFCommitOnBlurNumberInput
          name={`${FIELD_PATH}.${index}.height`}
          label='Height'
          placeholder='0'
          aiIconOnly={true}
          hideAIHint={catalogAppliedFields.has('height')}
          min={0}
          step={1}
          inlineDimensionUnit={dimensionUnit}
          data-testid={`${FIELD_PATH}.${index}.height`}
        />
      </Grid>

      <Grid cols='2' gap='lg' align='end' className='mb-4 w-full mx-0'>
        <RHFCommitOnBlurNumberInput
          name={`${FIELD_PATH}.${index}.handlingUnits`}
          label='Handling'
          placeholder='1'
          min={1}
          step={1}
          aiIconOnly={true}
          hideAIHint={catalogAppliedFields.has('handlingUnits')}
          data-testid={`${FIELD_PATH}.${index}.handlingUnits`}
        />
        <div className='min-w-0'>
          <Label
            name={`${FIELD_PATH}.${index}.packageType`}
            aiIconOnly={true}
            hideAIHint={catalogAppliedFields.has('packageType')}
            className='min-w-0'
          >
            Package Type
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.packageType`}
            control={control}
            render={({ field }) => {
              const currentPackageType =
                field.value !== undefined &&
                field.value !== null &&
                PACKAGE_CODE_TO_TYPE[field.value]
                  ? PACKAGE_CODE_TO_TYPE[field.value]
                  : 'Pallet';
              return (
                <Select
                  onValueChange={(value) => {
                    const numericCode = PACKAGE_TYPE_TO_CODE[value];
                    if (numericCode !== undefined) {
                      field.onChange(numericCode);
                    }
                  }}
                  value={currentPackageType}
                >
                  <SelectTrigger className='w-full mt-1'>
                    <SelectValue placeholder='Select type' />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(PACKAGE_TYPE_TO_CODE).map((packageType) => (
                      <SelectItem key={packageType} value={packageType}>
                        {packageType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />
        </div>
      </Grid>

      <Grid cols='2' gap='lg' className='w-full mx-0 mb-4'>
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.pieces`}
          label='Pieces'
          placeholder='1'
          inputType='number'
          aiIconOnly={true}
          hideAIHint={catalogAppliedFields.has('pieces')}
          options={{ valueAsNumber: true, min: 1 }}
          required={false}
          step={1}
        />
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.weight`}
          label='Total Weight'
          placeholder='0'
          inputType='number'
          aiIconOnly={true}
          hideAIHint={catalogAppliedFields.has('weight')}
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          inlineDimensionUnit={weightUnit}
          step={1}
        />
      </Grid>

      {/* Tai freight class warning (per item) */}
      {taiWarning?.showWarning && (
        <div className='relative p-3 pr-10 bg-warning-100 border border-warning-500 rounded-md mb-4'>
          <Flex align='start' gap='sm'>
            <TriangleAlert className='h-4 w-4 text-warning-600 shrink-0 mt-0.5' />
            <Flex direction='col' gap='xs' className='flex-1'>
              <Typography variant='body-xs' className='text-neutral-800'>
                The estimated class based on this item&apos;s dimensions differs
                from the class you have selected.
              </Typography>
              <button
                type='button'
                onClick={taiWarning?.handleUseEstimatedClass}
                className='text-brand-600 hover:text-brand-700 underline font-medium text-xs text-left cursor-pointer'
              >
                Click to use the estimated class (
                {taiWarning?.calculatedFreightClass})
              </button>
            </Flex>
          </Flex>
          <Button
            type='button'
            variant='ghost'
            size='auto'
            onClick={taiWarning?.handleDismissWarning}
            className='absolute top-2 right-2 h-6 w-6 p-0'
            buttonNamePosthog={null}
          >
            <XIcon className='h-4 w-4 text-neutral-600' />
          </Button>
        </div>
      )}
    </motion.div>
  );

  const renderViewMode = () => {
    if (!watchedItem?.description) {
      return null;
    }

    const freightClass = watchedItem.freightClass;
    const nmfcCode = watchedItem.nmfcCode;

    const hasHandlingUnits = isValidNumber(watchedItem.handlingUnits);
    const hasPieces = isValidNumber(watchedItem.pieces);
    const hasWeight = isValidNumber(watchedItem.weight);
    const hasPackageType = watchedItem.packageType !== undefined;
    const dimRow = normalizedItemForMetrics[0];
    const dimensions = dimRow
      ? [dimRow.length, dimRow.width, dimRow.height].filter(
          (d): d is number => typeof d === 'number' && d > 0
        )
      : [];
    const hasDimensions = dimensions.length > 0;

    const parts: JSX.Element[] = [];
    if (hasHandlingUnits) {
      parts.push(
        <Typography key='hu' variant='body-xs' className='text-neutral-500'>
          HU: {watchedItem.handlingUnits}
        </Typography>
      );
    }
    if (hasPieces) {
      parts.push(
        <Typography key='pc' variant='body-xs' className='text-neutral-500'>
          Pieces: {watchedItem.pieces}
        </Typography>
      );
    }
    if (hasDimensions) {
      parts.push(
        <Typography key='dims' variant='body-xs' className='text-neutral-500'>
          Dims: {dimensions.join(' × ')} {dimensionUnit}
        </Typography>
      );
    }
    if (hasWeight) {
      parts.push(
        <Typography key='wt' variant='body-xs' className='text-neutral-500'>
          Weight: {watchedItem.weight} {weightUnit}
        </Typography>
      );
    }
    if (hasPackageType) {
      parts.push(
        <Typography key='pkg' variant='body-xs' className='text-neutral-500'>
          Type:{' '}
          {PACKAGE_CODE_TO_TYPE[watchedItem.packageType!] ||
            watchedItem.packageType}
        </Typography>
      );
    }
    // Second line: NMFC, FC, Cubic Feet, Density (PCF) - same line as NMFC and FC
    const secondLineParts: JSX.Element[] = [];
    if (nmfcCode) {
      secondLineParts.push(
        <Typography key='nmfc' variant='body-xs' className='text-neutral-500'>
          NMFC {nmfcCode}
        </Typography>
      );
    }
    if (freightClass) {
      secondLineParts.push(
        <Typography key='fc' variant='body-xs' className='text-neutral-500'>
          FC: {freightClass}
        </Typography>
      );
    }
    const totalVolumeForSummary = isMetric
      ? itemMetrics.totalCubicFeet * CUBIC_METERS_PER_CUBIC_FOOT
      : itemMetrics.totalCubicFeet;

    if (totalVolumeForSummary > 0) {
      secondLineParts.push(
        <Typography key='cbf' variant='body-xs' className='text-neutral-500'>
          {isMetric
            ? `CBM: ${formatUpToFiveDecimals(totalVolumeForSummary)} m³`
            : `Cubic ft: ${formatUpToFiveDecimals(totalVolumeForSummary)} ft³`}
        </Typography>
      );
    }
    if (itemMetrics.pcf > 0) {
      secondLineParts.push(
        <Typography key='pcf' variant='body-xs' className='text-neutral-500'>
          PCF: {formatUpToFiveDecimals(itemMetrics.pcf)} lbs/ft³
        </Typography>
      );
    }

    return (
      <motion.div
        key='view-mode'
        {...ANIMATIONS.toggle}
        className='overflow-hidden cursor-pointer'
        onClick={handlers.edit}
        onKeyDown={(e: React.KeyboardEvent) =>
          e.key === 'Enter' && handlers.edit()
        }
        role='button'
        tabIndex={0}
        aria-label='Click to edit item'
      >
        <Flex justify='between' align='center' className='min-w-0'>
          <Flex align='center' gap='sm' className='flex-1 min-w-0 mr-2'>
            <Package className='h-4 w-4 text-neutral-500 shrink-0' />
            <Typography
              variant='body-sm'
              weight='medium'
              className='truncate'
              title={watchedItem.description}
            >
              {watchedItem.description}
            </Typography>
            {renderSideLoadStatusTag('max-w-[170px]')}
          </Flex>
          <div onClick={(e) => e.stopPropagation()} className='shrink-0'>
            {renderActions()}
          </div>
        </Flex>
        {parts.length > 0 && (
          <Flex align='center' gap='sm' className='mt-1 ml-1 flex-wrap'>
            {parts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className='text-neutral-300'>•</span>}
                {p}
              </React.Fragment>
            ))}
          </Flex>
        )}
        {secondLineParts.length > 0 && (
          <Flex align='center' gap='sm' className='mt-0.5 ml-1 flex-wrap'>
            {secondLineParts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className='text-neutral-300'>•</span>}
                {p}
              </React.Fragment>
            ))}
          </Flex>
        )}
      </motion.div>
    );
  };

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className='w-full overflow-hidden rounded-md border border-neutral-400 shadow-sm relative p-3 mb-2'
      style={
        showVisualizationColor
          ? {
              borderLeft: `3px solid ${getColorForItemId(
                watchedItem?.itemId ?? `item-${index}`,
                itemColorMap
              )}`,
            }
          : undefined
      }
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
